import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Calculator, 
  TrendingUp, 
  DollarSign, 
  ArrowRight,
  CheckCircle,
  Sparkles,
  Target,
  Zap
} from 'lucide-react';

const ROICalculator = ({ language = "es", isVisible, onClose, onShowSignUp }) => {
  const [formData, setFormData] = useState({
    numberOfTables: 12,
    ordersPerDay: 100,
    avgOrderValue: 25,
    reviewRate: 10,
    numberOfLocations: 1,
    hasWaiters: true,
    hasBar: true,
    currentMonthlyRevenue: 0
  });

  const [results, setResults] = useState(null);
  const [recommendedTier, setRecommendedTier] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [painPoints, setPainPoints] = useState([]);

  const content = {
    es: {
      title: "Calcular mi Plan KumIA",
      subtitle: "Descubre tu Transformación Personalizada y el Potencial Real de tu Restaurante",
      form: {
        numberOfTables: "¿Cuántas mesas tienes en tu restaurante?",
        numberOfLocations: "¿Cuántas sucursales/locales tienes?",
        hasWaiters: "¿Tienes mesoneros/camareros?",
        hasBar: "¿Tienes bar en tu restaurante?",
        ordersPerDay: "Pedidos promedio por día",
        avgOrderValue: "Ticket promedio por mesa (USD)",
        reviewRate: "¿Qué % de tus clientes actualmente te dejan reviews mensuales?",
        calculate: "Ver mi Transformación KumIA"
      },
      steps: {
        analysis: "Análisis de tu Restaurante",
        pain: "Puntos de Dolor Actuales", 
        transformation: "Tu Transformación KumIA",
        outcome: "Resultado Final"
      },
      tiers: {
        freemium: "Freemium",
        pro: "PRO",
        allin: "All-In", 
        enterprise: "Enterprise"
      },
      painAnalysis: {
        title: "💰 EL COSTO REAL DE LA INACCIÓN",
        strategicQuestionsTitle: "🎯 PREGUNTAS ESTRATÉGICAS QUE DEFINEN TU FUTURO:",
        problems: {
          lowReviews: "🔴 Con {reviewRate}% de reviews, ¿dónde están los otros {missingReviews} clientes mensuales que NO hablan de ti?",
          lostRevenue: "💸 Pierdes ${lostRevenue}/mes mientras tus competidores con IA capturan TU mercado",
          reviewGap: "📊 Si tienes {monthlyOrders} órdenes/mes, ¿por qué solo {currentReviews} reviews? ¿Dónde están los otros {missingReviews}?",
          reputationCost: "🏆 ¿Te imaginas tener {monthlyOrders} reviews mensuales? ¿Cómo te posicionaría vs tu competencia?",
          ignoranceCost: "🎯 Cada cliente que sale sin dejar review es una oportunidad de oro perdida para siempre"
        },
        questions: {
          reviews: "💎 ¿Puedes PERMITIRTE seguir ignorando que el 87% de decisiones de compra se basan en reviews?",
          survival: "⚡ PREGUNTA CLAVE: ¿Puedes SOBREVIVIR seguir perdiendo ${dailyLoss}/día mientras esperas?",
          competition: "🚨 ¿Qué pasará cuando tu competencia tenga IA y tú sigas manual?",
          roi: "🎯 PENSADORES DE ROI INVIERTEN ESTRATÉGICAMENTE PARA PROSPERAR. ¿Cuál eres tú?",
          multiplier: "💰 ¿Cuál es el multiplicador de dinero si cada cliente se convierte en tu vendedor automático?"
        }
      },
      transformation: {
        title: "🚀 TU TRANSFORMACIÓN CON KUMIA",
        subtitle: "Imagina tu restaurante finalmente entregándote la LIBERTAD que siempre soñaste...",
        dreams: {
          title: "💫 TUS SUEÑOS SE HACEN REALIDAD",
          single: "🌟 Imagínate abriendo esa segunda sucursal que siempre quisiste",
          multiple: "🌍 Escalando a otras ciudades, incluso otros países",
          freedom: "🏖️ Restaurante funcionando perfecto mientras estás de vacaciones",
          team: "💰 Tu equipo ganando más dinero, más felices, más productivos",
          customers: "❤️ Clientes que se vuelven fanáticos y traen a toda su familia"
        },
        outcomes: {
          reviews: "🌟 {targetReviews} reviews automáticas mensuales GARANTIZADAS (mínimo 50% de tus órdenes vs {currentReviews} actuales)",
          revenue: "💰 +${additionalRevenue}/mes GARANTIZADOS por personalización con IA",
          efficiency: "⚡ 40% más eficiente - tu equipo trabajará como máquina perfecta",
          data: "🧠 IA que conoce a cada cliente mejor que tú mismo",
          competitive: "🏆 El ÚNICO restaurante con IA completa en toda tu zona"
        },
        vision: "Tu restaurante se convierte en esa experiencia mágica que la gente recomienda sin parar. Cada cliente se siente especial, cada visita es perfecta, y tú finalmente tienes el negocio que genera dinero mientras duermes."
      },
      results: {
        title: "🎯 TU POTENCIAL TRANSFORMADO",
        recommendedPlan: "📋 PLAN RECOMENDADO PARA TI:",
        planExplanation: "Basado en tu restaurante ({numberOfLocations} {numberOfLocations === 1 ? 'local' : 'locales'}, {hasWaiters ? 'con' : 'sin'} mesoneros, {hasBar ? 'con' : 'sin'} bar):",
        planExplanationTemplate: "Basado en tu restaurante",
        planQuestion: "¿No invertirías ${costPerOrder} USD por orden completada para fidelizar a tus clientes con un ROI de {roi}%?",
        monthlyOrders: "Pedidos mensuales",
        baseFee: "Costo Fijo Mensual",
        variableFees: "Costos Variables/mes",
        totalInvestment: "Inversión Total/mes", 
        additionalRevenue: "Ingresos adicionales/mes",
        netProfit: "GANANCIA NETA/mes",
        roi: "Retorno",
        paybackTime: "Se paga solo en",
        getStarted: "¡Evolucionar mi Restaurant YA!",
        benefits: {
          efficiency: "+40% eficiencia operacional",
          satisfaction: "+60% satisfacción cliente", 
          upselling: "+35% ticket promedio por IA",
          retention: "+85% clientes recurrentes",
          reviews: "Reviews automáticas en Google"
        },
        planReasons: {
          enterprise: "Múltiples locales requieren coordinación enterprise y APIs avanzadas",
          allinMultiple: "Múltiples locales con operación completa necesitan el ecosistema completo", 
          proBasic: "Sin mesoneros ni bar, perfecto para fast food/food trucks",
          allinComplete: "Restaurante completo necesita coordinación perfecta entre todas las áreas",
          proIntermediate: "Operación intermedia, ideal para crecimiento controlado"
        }
      },
      ui: {
        yes: "Sí",
        no: "No", 
        tables: "mesas",
        ordersPerDay: "pedidos/día",
        location: "local",
        locations: "locales",
        preview: "Vista Previa",
        restaurantData: "Datos de tu Restaurante",
        locationLabel: "Locales",
        waitersLabel: "Mesoneros",
        tablesLabel: "Mesas",
        barLabel: "Bar",
        ordersMonthLabel: "Pedidos/mes",
        avgTicketLabel: "Ticket promedio",
        planExplanationTemplate: "Basado en tu restaurante",
        with: "con",
        without: "sin",
        seeTransformation: "Ver mi Transformación",
        transformedVision: "TU VISIÓN TRANSFORMADA",
        seeRecommendedPlan: "Ver Plan Recomendado",
        costPerOrder: "por orden completada (costo real por uso)",
        roiCalculation: "Cálculo del Retorno",
        roiFormula: "(Ganancia Neta / Inversión Total) × 100",
        roiDescription: "Basado en ingresos adicionales vs costo total KumIA"
      }
    },
    en: {
      title: "Calculate my KumIA Plan",
      subtitle: "Discover your Personalized Transformation and Restaurant's Real Potential",
      form: {
        numberOfLocations: "How many locations/branches do you have?",
        hasWaiters: "Do you have waiters/servers?",
        hasBar: "Do you have a bar in your restaurant?",
        numberOfTables: "How many tables do you have in your restaurant?",
        ordersPerDay: "Average orders per day",
        avgOrderValue: "Average ticket per table (USD)",
        reviewRate: "What % of your customers currently leave monthly reviews?",
        calculate: "See my KumIA Transformation"
      },
      steps: {
        analysis: "Restaurant Analysis",
        pain: "Current Pain Points",
        transformation: "Your KumIA Transformation", 
        outcome: "Final Result"
      },
      tiers: {
        freemium: "Freemium",
        pro: "PRO",
        allin: "All-In",
        enterprise: "Enterprise"
      },
      painAnalysis: {
        title: "💰 THE REAL COST OF INACTION",
        subtitle: "How much are you losing every month by not having AI automation?",
        strategicQuestionsTitle: "🎯 STRATEGIC QUESTIONS THAT DEFINE YOUR FUTURE:",
        problems: {
          lowReviews: "🔴 With {reviewRate}% reviews, where are the other {missingReviews} monthly customers who DON'T talk about you?",
          lostRevenue: "💸 You lose ${lostRevenue}/month while your AI competitors capture YOUR market",
          reviewGap: "📊 If you have {monthlyOrders} orders/month, why only {currentReviews} reviews? Where are the other {missingReviews}?",
          reputationCost: "🏆 Can you imagine having {monthlyOrders} monthly reviews? How would that position you vs your competition?",
          ignoranceCost: "🎯 Every customer who leaves without a review is a golden opportunity lost forever"
        },
        questions: {
          reviews: "💎 Can you AFFORD to keep ignoring that 87% of purchase decisions are based on reviews?",
          survival: "⚡ KEY QUESTION: Can you SURVIVE losing ${dailyLoss}/day while you wait?",
          competition: "🚨 What happens when your competition has AI and you're still manual?",
          roi: "🎯 ROI THINKERS INVEST STRATEGICALLY TO PROSPER. Which one are you?",
          multiplier: "💰 What's the money multiplier if every customer becomes your automatic salesperson?"
        }
      },
      transformation: {
        title: "🚀 YOUR TRANSFORMATION WITH KUMIA",
        subtitle: "Imagine your restaurant finally giving you the FREEDOM you always dreamed of...",
        dreams: {
          title: "💫 YOUR DREAMS COME TRUE",
          single: "🌟 Imagine opening that second location you always wanted",
          multiple: "🌍 Scaling to other cities, even other countries",
          freedom: "🏖️ Restaurant running perfectly while you're on vacation",
          team: "💰 Your team earning more money, happier, more productive",
          customers: "❤️ Customers who become fanatics and bring their whole family"
        },
        outcomes: {
          reviews: "🌟 {targetReviews} automatic monthly reviews GUARANTEED (minimum 50% of your orders vs {currentReviews} current)",
          revenue: "💰 +${additionalRevenue}/month in GUARANTEED additional income",
          efficiency: "⚡ 40% more efficient - more orders with same staff",
          data: "🧠 AI that knows each customer and personalizes automatically",
          competitive: "🏆 The only restaurant with complete AI in your area"
        },
        vision: "Instead of being just another restaurant, you become a personalized EXPERIENCE that creates addiction and automatic recommendations. Every customer is 85% more loyal and spends 35% more due to AI personalization."
      },
      results: {
        title: "🎯 YOUR TRANSFORMED POTENTIAL",
        monthlyOrders: "Monthly orders",
        kumiaFees: "KumIA Investment/month",
        additionalRevenue: "Additional Revenue/month",
        netProfit: "NET PROFIT/month",
        roi: "Return",
        paybackTime: "Pays for itself in",
        getStarted: "Evolve my Restaurant NOW!",
        planQuestion: "Wouldn't you invest ${costPerOrder} USD per completed order to retain your customers with a {roi}% ROI?",
        benefits: {
          efficiency: "+40% operational efficiency",
          satisfaction: "+60% customer satisfaction",
          upselling: "+35% average ticket with AI",
          retention: "+85% recurring customers", 
          reviews: "Automatic Google reviews"
        },
        planReasons: {
          enterprise: "Multiple locations require enterprise coordination and advanced APIs",
          allinMultiple: "Multiple locations with complete operation need the full ecosystem", 
          proBasic: "Without waiters or bar, perfect for fast food/food trucks",
          allinComplete: "Complete restaurant needs perfect coordination between all areas",
          proIntermediate: "Intermediate operation, ideal for controlled growth"
        }
      },
      ui: {
        yes: "Yes",
        no: "No", 
        tables: "tables",
        ordersPerDay: "orders/day",
        location: "location",
        locations: "locations",
        preview: "Preview",
        restaurantData: "Restaurant Data",
        locationLabel: "Locations",
        waitersLabel: "Waiters",
        tablesLabel: "Tables",
        barLabel: "Bar",
        ordersMonthLabel: "Orders/month",
        avgTicketLabel: "Average ticket",
        planExplanationTemplate: "Based on your restaurant",
        with: "with",
        without: "without",
        seeTransformation: "See my Transformation",
        transformedVision: "YOUR TRANSFORMED VISION",
        seeRecommendedPlan: "See Recommended Plan",
        costPerOrder: "per completed order (actual usage cost)",
        roiCalculation: "Return Calculation",
        roiFormula: "(Net Profit / Total Investment) × 100",
        roiDescription: "Based on additional revenue vs total KumIA cost"
      }
    },
    pt: {
      title: "Calcular meu Plano KumIA",
      subtitle: "Descubra sua Transformação Personalizada e o Potencial Real do seu Restaurante",
      form: {
        numberOfLocations: "Quantos locais/filiais você tem?",
        hasWaiters: "Você tem garçons/atendentes?",
        hasBar: "Você tem bar no seu restaurante?",
        numberOfTables: "Quantas mesas você tem no seu restaurante?",
        ordersPerDay: "Pedidos médios por dia",
        avgOrderValue: "Ticket médio por mesa (USD)",
        reviewRate: "Que % dos seus clientes atualmente deixam avaliações mensais?",
        calculate: "Ver minha Transformação KumIA"
      },
      steps: {
        analysis: "Análise do Restaurante",
        pain: "Pontos de Dor Atuais",
        transformation: "Sua Transformação KumIA",
        outcome: "Resultado Final"
      },
      tiers: {
        freemium: "Freemium",
        pro: "PRO",
        allin: "All-In",
        enterprise: "Enterprise"
      },
      painAnalysis: {
        title: "💰 O CUSTO REAL DA INAÇÃO",
        subtitle: "Quanto você está perdendo por mês ao não ter automação de IA?",
        strategicQuestionsTitle: "🎯 PERGUNTAS ESTRATÉGICAS QUE DEFINEM SEU FUTURO:",
        problems: {
          lowReviews: "🔴 Com {reviewRate}% de avaliações, onde estão os outros {missingReviews} clientes mensais que NÃO falam de você?",
          lostRevenue: "💸 Você perde R${lostRevenue}/mês enquanto seus concorrentes com IA capturam SEU mercado",
          reviewGap: "📊 Se você tem {monthlyOrders} pedidos/mês, por que apenas {currentReviews} avaliações? Onde estão as outras {missingReviews}?",
          reputationCost: "🏆 Você consegue imaginar ter {monthlyOrders} avaliações mensais? Como isso te posicionaria contra sua concorrência?",
          ignoranceCost: "🎯 Cada cliente que sai sem deixar avaliação é uma oportunidade de ouro perdida para sempre"
        },
        questions: {
          reviews: "💎 Você pode se dar ao luxo de continuar ignorando que 87% das decisões de compra são baseadas em avaliações?",
          survival: "⚡ PERGUNTA CHAVE: Você pode SOBREVIVER perdendo R${dailyLoss}/dia enquanto espera?",
          competition: "🚨 O que acontece quando sua concorrência tem IA e você ainda está manual?",
          roi: "🎯 PENSADORES DE ROI INVESTEM ESTRATEGICAMENTE PARA PROSPERAR. Qual é você?",
          multiplier: "💰 Qual é o multiplicador de dinheiro se cada cliente se torna seu vendedor automático?"
        }
      },
      transformation: {
        title: "🚀 SUA TRANSFORMAÇÃO COM KUMIA",
        subtitle: "Imagine seu restaurante finalmente te dando a LIBERDADE que você sempre sonhou...",
        dreams: {
          title: "💫 SEUS SONHOS SE TORNAM REALIDADE",
          single: "🌟 Imagine abrindo aquela segunda filial que você sempre quis",
          multiple: "🌍 Expandindo para outras cidades, até outros países",
          freedom: "🏖️ Restaurante funcionando perfeitamente enquanto você está de férias",
          team: "💰 Sua equipe ganhando mais dinheiro, mais feliz, mais produtiva",
          customers: "❤️ Clientes que se tornam fanáticos e trazem toda a família"
        },
        outcomes: {
          reviews: "🌟 {targetReviews} avaliações automáticas mensais GARANTIDAS (mínimo 50% dos seus pedidos vs {currentReviews} atuais)",
          revenue: "💰 +${additionalRevenue}/mês em receita adicional GARANTIDA",
          efficiency: "⚡ 40% mais eficiente - mais pedidos com a mesma equipe",
          data: "🧠 IA que conhece cada cliente e personaliza automaticamente",
          competitive: "🏆 O único restaurante com IA completa na sua região"
        },
        vision: "Em vez de ser mais um restaurante, você se torna uma EXPERIÊNCIA personalizada que gera vício e recomendações automáticas. Cada cliente é 85% mais leal e gasta 35% mais pela personalização com IA."
      },
      results: {
        title: "🎯 SEU POTENCIAL TRANSFORMADO",
        monthlyOrders: "Pedidos mensais",
        kumiaFees: "Investimento KumIA/mês",
        additionalRevenue: "Receita adicional/mês",
        netProfit: "LUCRO LÍQUIDO/mês",
        roi: "Retorno",
        paybackTime: "Se paga sozinho em",
        getStarted: "Começar minha Transformação!",
        planQuestion: "Você não investiria ${costPerOrder} USD por pedido concluído para fidelizar seus clientes com um ROI de {roi}%?",
        benefits: {
          efficiency: "+40% eficiência operacional",
          satisfaction: "+60% satisfação do cliente",
          upselling: "+35% ticket médio com IA",
          retention: "+85% clientes recorrentes",
          reviews: "Avaliações automáticas no Google"
        },
        planReasons: {
          enterprise: "Múltiplos locais requerem coordenação enterprise e APIs avançadas",
          allinMultiple: "Múltiplos locais com operação completa precisam do ecossistema completo", 
          proBasic: "Sem garçons nem bar, perfeito para fast food/food trucks",
          allinComplete: "Restaurante completo precisa de coordenação perfeita entre todas as áreas",
          proIntermediate: "Operação intermediária, ideal para crescimento controlado"
        }
      },
      ui: {
        yes: "Sim",
        no: "Não", 
        tables: "mesas",
        ordersPerDay: "pedidos/dia",
        location: "local",
        locations: "locais",
        preview: "Visualização",
        restaurantData: "Dados do Restaurante",
        locationLabel: "Locais",
        waitersLabel: "Garçons",
        tablesLabel: "Mesas",
        barLabel: "Bar",
        ordersMonthLabel: "Pedidos/mês",
        avgTicketLabel: "Ticket médio",
        planExplanationTemplate: "Baseado no seu restaurante",
        with: "com",
        without: "sem",
        seeTransformation: "Ver minha Transformação",
        transformedVision: "SUA VISÃO TRANSFORMADA",
        seeRecommendedPlan: "Ver Plano Recomendado",
        costPerOrder: "por pedido concluído (custo real de uso)",
        roiCalculation: "Cálculo do Retorno",
        roiFormula: "(Lucro Líquido / Investimento Total) × 100",
        roiDescription: "Baseado em receita adicional vs custo total KumIA"
      }
    }
  };

  // Get current translations based on language
  const t = content[language] || content.es;

  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      currentMonthlyRevenue: prev.ordersPerDay * prev.avgOrderValue * 30
    }));
  }, [formData.ordersPerDay, formData.avgOrderValue]);

  const calculateTransformation = () => {
    const monthlyOrders = formData.ordersPerDay * 30;
    const currentRevenue = monthlyOrders * formData.avgOrderValue;
    const currentReviews = Math.round(monthlyOrders * (formData.reviewRate / 100));
    
    // INTELLIGENT PLAN RECOMMENDATION LOGIC
    let recommendedPlan = '';
    let planReason = '';
    
    if (formData.numberOfLocations >= 4) {
      recommendedPlan = 'Enterprise';
      planReason = t.results.planReasons.enterprise;
    } else if (formData.numberOfLocations >= 2 && formData.hasWaiters && formData.hasBar) {
      recommendedPlan = 'All-In';
      planReason = t.results.planReasons.allinMultiple;
    } else if (!formData.hasWaiters && !formData.hasBar) {
      recommendedPlan = 'PRO';
      planReason = t.results.planReasons.proBasic;
    } else if (formData.hasWaiters && formData.hasBar && formData.numberOfLocations === 1) {
      recommendedPlan = 'All-In';
      planReason = t.results.planReasons.allinComplete;
    } else {
      recommendedPlan = 'PRO';
      planReason = t.results.planReasons.proIntermediate;
    }
    
    setRecommendedTier(recommendedPlan);
    
    // KumIA pricing based on recommended tier
    let baseFee = 0;
    let orderFee = 0;
    let reviewFee = 0.10;
    let costPerOrder = 0;
    
    if (recommendedPlan === 'PRO') {
      baseFee = 69;
      orderFee = monthlyOrders * 0.20;
      costPerOrder = 0.20 + reviewFee; // $0.20 + $0.10 = $0.30
    } else if (recommendedPlan === 'All-In') {
      baseFee = 99;
      orderFee = monthlyOrders * 0.40;
      costPerOrder = 0.40 + reviewFee; // $0.40 + $0.10 = $0.50
    } else {
      baseFee = 299;
      orderFee = monthlyOrders * 0.30;
      costPerOrder = 0.30 + reviewFee; // $0.30 + $0.10 = $0.40
    }
    
    // Transformation calculations - MINIMUM 50% review conversion guaranteed
    const targetReviewRate = Math.max(50, Math.min(formData.reviewRate * 8, 85)); // Minimum 50%, up to 85%
    const targetReviews = Math.round(monthlyOrders * (targetReviewRate / 100));
    const reviewCost = targetReviews * reviewFee;
    
    const totalKumiaFees = baseFee + orderFee + reviewCost;
    
    // Massive additional revenue from transformation
    const lostRevenue = currentRevenue * 0.45;
    const efficiencyGains = currentRevenue * 0.40;
    const upsellIncrease = currentRevenue * 0.35;
    const retentionIncrease = currentRevenue * 0.50;
    const reviewsImpact = currentRevenue * 0.25;
    
    const totalAdditionalRevenue = efficiencyGains + upsellIncrease + retentionIncrease + reviewsImpact;
    const netProfit = totalAdditionalRevenue - totalKumiaFees;
    const roi = ((netProfit / totalKumiaFees) * 100);
    const paybackDays = Math.ceil(totalKumiaFees / (netProfit / 30));
    
    // Generate enhanced strategic pain points
    const missingReviews = monthlyOrders - currentReviews;
    const dailyLoss = Math.round(lostRevenue / 30);
    
    const generatedPainPoints = [
      t.painAnalysis.problems.lowReviews
        .replace('{reviewRate}', formData.reviewRate)
        .replace('{missingReviews}', missingReviews),
      t.painAnalysis.problems.lostRevenue.replace('{lostRevenue}', formatCurrency(lostRevenue)),
      t.painAnalysis.problems.reviewGap
        .replace('{monthlyOrders}', monthlyOrders)
        .replace('{currentReviews}', currentReviews)
        .replace('{missingReviews}', missingReviews),
      t.painAnalysis.problems.reputationCost.replace('{monthlyOrders}', monthlyOrders),
      t.painAnalysis.problems.ignoranceCost
    ];
    
    // Update questions with dynamic values
    const strategicQuestions = [
      t.painAnalysis.questions.reviews,
      t.painAnalysis.questions.survival.replace('{dailyLoss}', formatCurrency(dailyLoss)),
      t.painAnalysis.questions.competition,
      t.painAnalysis.questions.roi,
      t.painAnalysis.questions.multiplier
    ];
    
    setPainPoints(generatedPainPoints);
    setCurrentStep(1);
    
    setResults({
      monthlyOrders,
      kumiaFees: totalKumiaFees,
      baseFee,
      variableFees: orderFee + reviewCost,
      additionalRevenue: totalAdditionalRevenue,
      netProfit,
      roi,
      paybackTime: paybackDays,
      currentRevenue,
      currentReviews,
      targetReviews,
      lostRevenue,
      recommendedPlan,
      planReason,
      strategicQuestions,
      missingReviews,
      dailyLoss,
      costPerOrder
    });
  };

  const nextStep = () => {
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    } else {
      // Final CTA - Start transformation - Show SignUp modal
      onShowSignUp && onShowSignUp();
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  if (!isVisible) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="relative p-8 bg-gradient-to-br from-[#9ACD32] to-green-600 text-black rounded-t-3xl">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-full bg-black/20 hover:bg-black/30 transition-colors text-black font-bold"
          >
            <span className="text-xl">×</span>
          </button>
          
          <div className="flex items-center space-x-4 mb-4">
            <Calculator className="w-12 h-12 text-black" />
            <div>
              <h2 className="text-2xl md:text-3xl font-black text-black">{t.title}</h2>
              <p className="text-sm md:text-base text-black/80 font-semibold">{t.subtitle}</p>
            </div>
          </div>
          
          {/* Progress Steps */}
          <div className="flex justify-center space-x-4 mt-6">
            {['analysis', 'pain', 'transformation', 'outcome'].map((step, index) => (
              <div key={step} className={`flex items-center ${index < 3 ? 'mr-2' : ''}`}>
                <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm ${
                  currentStep >= index 
                    ? 'bg-black text-[#9ACD32]' 
                    : 'bg-black/20 text-black/60'
                }`}>
                  {index + 1}
                </div>
                <span className={`ml-2 text-sm font-semibold hidden md:block ${
                  currentStep >= index ? 'text-black' : 'text-black/60'
                }`}>
                  {t.steps[step]}
                </span>
                {index < 3 && <div className="w-8 h-0.5 bg-black/30 mx-2 hidden md:block" />}
              </div>
            ))}
          </div>
        </div>

        <div className="p-8">
          {/* Step 0: Analysis Form */}
          {currentStep === 0 && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="grid md:grid-cols-2 gap-8"
            >
              <div>
                <h3 className="text-lg md:text-2xl font-bold text-gray-800 mb-6 flex items-center">
                  <Target className="w-5 h-5 md:w-6 md:h-6 mr-2 text-[#9ACD32]" />
                  <span className="text-sm md:text-base">📊 {t.ui.restaurantData}</span>
                </h3>
                
                {/* Input Fields */}
                <div className="space-y-6">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      {t.form.numberOfLocations}
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="20"
                      step="1"
                      value={formData.numberOfLocations}
                      onChange={(e) => setFormData({...formData, numberOfLocations: parseInt(e.target.value)})}
                      className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-2">
                      <span>1 {t.ui.location}</span>
                      <span className="font-bold text-[#9ACD32] text-lg">{formData.numberOfLocations} {formData.numberOfLocations === 1 ? t.ui.location : t.ui.locations}</span>
                      <span>20 {t.ui.locations}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="h-full flex flex-col">
                      <label className="block text-sm font-semibold text-gray-700 mb-3 flex-shrink-0">
                        {t.form.hasWaiters}
                      </label>
                      <div className="flex space-x-2 flex-grow items-end">
                        <button
                          onClick={() => setFormData({...formData, hasWaiters: true})}
                          className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
                            formData.hasWaiters
                              ? 'bg-gradient-to-r from-[#9ACD32] to-green-600 text-black shadow-lg'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          ✅ {t.ui.yes}
                        </button>
                        <button
                          onClick={() => setFormData({...formData, hasWaiters: false})}
                          className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
                            !formData.hasWaiters
                              ? 'bg-gradient-to-r from-[#9ACD32] to-green-600 text-black shadow-lg'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          ❌ {t.ui.no}
                        </button>
                      </div>
                    </div>

                    <div className="h-full flex flex-col">
                      <label className="block text-sm font-semibold text-gray-700 mb-3 flex-shrink-0">
                        {t.form.hasBar}
                      </label>
                      <div className="flex space-x-2 flex-grow items-end">
                        <button
                          onClick={() => setFormData({...formData, hasBar: true})}
                          className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
                            formData.hasBar
                              ? 'bg-gradient-to-r from-[#9ACD32] to-green-600 text-black shadow-lg'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          🍹 {t.ui.yes}
                        </button>
                        <button
                          onClick={() => setFormData({...formData, hasBar: false})}
                          className={`flex-1 px-4 py-3 rounded-lg font-medium transition-all ${
                            !formData.hasBar
                              ? 'bg-gradient-to-r from-[#9ACD32] to-green-600 text-black shadow-lg'
                              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                          }`}
                        >
                          ❌ {t.ui.no}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      {t.form.numberOfTables}
                    </label>
                    <input
                      type="range"
                      min="4"
                      max="50"
                      step="2"
                      value={formData.numberOfTables}
                      onChange={(e) => setFormData({...formData, numberOfTables: parseInt(e.target.value)})}
                      className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-2">
                      <span>4 {t.ui.tables}</span>
                      <span className="font-bold text-[#9ACD32] text-lg">{formData.numberOfTables} {t.ui.tables}</span>
                      <span>50 {t.ui.tables}</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      {t.form.ordersPerDay}
                    </label>
                    <input
                      type="range"
                      min="20"
                      max="500"
                      step="10"
                      value={formData.ordersPerDay}
                      onChange={(e) => setFormData({...formData, ordersPerDay: parseInt(e.target.value)})}
                      className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-2">
                      <span>20</span>
                      <span className="font-bold text-[#9ACD32] text-lg">{formData.ordersPerDay} {t.ui.ordersPerDay}</span>
                      <span>500</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      {t.form.avgOrderValue}
                    </label>
                    <input
                      type="range"
                      min="10"
                      max="100"
                      step="5"
                      value={formData.avgOrderValue}
                      onChange={(e) => setFormData({...formData, avgOrderValue: parseInt(e.target.value)})}
                      className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-2">
                      <span>$10</span>
                      <span className="font-bold text-[#9ACD32] text-lg">${formData.avgOrderValue}</span>
                      <span>$100</span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-3">
                      {t.form.reviewRate}
                    </label>
                    <input
                      type="range"
                      min="1"
                      max="40"
                      step="1"
                      value={formData.reviewRate}
                      onChange={(e) => setFormData({...formData, reviewRate: parseInt(e.target.value)})}
                      className="w-full h-3 bg-gray-200 rounded-lg appearance-none cursor-pointer slider"
                    />
                    <div className="flex justify-between text-sm text-gray-500 mt-2">
                      <span>1%</span>
                      <span className="font-bold text-[#9ACD32] text-lg">{formData.reviewRate}%</span>
                      <span>40%</span>
                    </div>
                  </div>
                </div>

                <motion.button
                  onClick={calculateTransformation}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full mt-8 bg-gradient-to-r from-[#9ACD32] to-green-600 text-black py-4 px-8 rounded-2xl font-black text-lg shadow-lg hover:shadow-xl transition-all flex items-center justify-center space-x-3"
                >
                  <Sparkles className="w-6 h-6" />
                  <span>{t.form.calculate}</span>
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </div>
              
              {/* Preview Panel */}
              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl p-4 md:p-6 h-fit">
                <h4 className="text-base md:text-lg font-bold text-gray-800 mb-4 text-center">📈 {t.ui.preview}</h4>
                <div className="space-y-3 text-xs md:text-sm">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t.ui.locationLabel}:</span>
                    <span className="font-bold text-gray-800">{formData.numberOfLocations}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t.ui.waitersLabel}:</span>
                    <span className={`font-bold ${formData.hasWaiters ? 'text-green-600' : 'text-red-600'}`}>
                      {formData.hasWaiters ? '✅ ' + t.ui.yes : '❌ ' + t.ui.no}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t.ui.barLabel}:</span>
                    <span className={`font-bold ${formData.hasBar ? 'text-green-600' : 'text-red-600'}`}>
                      {formData.hasBar ? '🍹 ' + t.ui.yes : '❌ ' + t.ui.no}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t.ui.tablesLabel}:</span>
                    <span className="font-bold text-gray-800">{formData.numberOfTables}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t.ui.ordersMonthLabel}:</span>
                    <span className="font-bold text-gray-800">{formData.ordersPerDay * 30}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">{t.ui.avgTicketLabel}:</span>
                    <span className="font-bold text-gray-800">${formData.avgOrderValue}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Reviews actuales:</span>
                    <span className="font-bold text-red-600">{Math.round(formData.ordersPerDay * 30 * (formData.reviewRate / 100))}/mes</span>
                  </div>
                  <hr className="my-4" />
                  <div className="flex justify-between items-center text-base md:text-lg">
                    <span className="text-gray-700">Ingresos actuales:</span>
                    <span className="font-black text-gray-900 text-sm md:text-base">${formatCurrency(formData.ordersPerDay * formData.avgOrderValue * 30).replace('$', '')}/mes</span>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Step 1: Pain Points Analysis */}
          {currentStep === 1 && results && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="bg-gradient-to-br from-red-50 to-orange-50 border-l-4 border-red-500 rounded-2xl p-6 md:p-8 mb-8">
                <h3 className="text-2xl md:text-3xl font-black text-red-700 mb-6">{t.painAnalysis.title}</h3>
                
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  {painPoints.map((pain, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.2 }}
                      className="bg-white/70 p-4 rounded-xl border border-red-200 text-left"
                    >
                      <p className="text-red-700 font-semibold">{pain}</p>
                    </motion.div>
                  ))}
                </div>

                <div className="bg-white/80 rounded-xl p-4 md:p-6 mb-6">
                  <h4 className="text-lg md:text-xl font-bold text-gray-800 mb-4">{t.painAnalysis.strategicQuestionsTitle}</h4>
                  <div className="space-y-3 text-left">
                    {[
                      t.painAnalysis.questions.reviews,
                      t.painAnalysis.questions.survival?.replace('{dailyLoss}', formatCurrency(results?.dailyLoss || 0)) || 'Loading...',
                      t.painAnalysis.questions.competition,
                      t.painAnalysis.questions.roi,
                      t.painAnalysis.questions.multiplier
                    ].map((question, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.5 + index * 0.1 }}
                        className="flex items-start space-x-3"
                      >
                        <span className="text-red-500 font-bold text-lg">•</span>
                        <p className="text-gray-700 font-medium text-sm md:text-base">{question}</p>
                      </motion.div>
                    ))}
                  </div>
                </div>

                <motion.button
                  onClick={nextStep}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-[#9ACD32] to-green-600 text-white px-12 py-4 rounded-2xl font-black text-lg shadow-2xl hover:shadow-xl transition-all flex items-center mx-auto space-x-3"
                >
                  <span>{t.ui.seeTransformation}</span>
                  <ArrowRight className="w-5 h-5" />
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Step 2: Transformation Vision */}
          {currentStep === 2 && results && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-l-4 border-[#9ACD32] rounded-2xl p-6 md:p-8 mb-8">
                <h3 className="text-3xl md:text-4xl font-black text-[#9ACD32] mb-4">{t.transformation.title}</h3>
                <p className="text-lg md:text-xl text-gray-700 font-semibold mb-8">{t.transformation.subtitle}</p>
                
                {/* Dreams Section Based on Business Type */}
                <div className="bg-gradient-to-r from-[#9ACD32]/20 to-green-200/30 rounded-2xl p-4 md:p-6 mb-8">
                  <h4 className="text-xl md:text-2xl font-black text-gray-800 mb-4">{t.transformation.dreams.title}</h4>
                  <div className="grid md:grid-cols-2 gap-4">
                    {formData.numberOfLocations === 1 && (
                      <div className="bg-white/80 p-3 md:p-4 rounded-xl">
                        <p className="text-green-700 font-bold text-base md:text-lg">{t.transformation.dreams.single}</p>
                      </div>
                    )}
                    {formData.numberOfLocations > 1 && (
                      <div className="bg-white/80 p-3 md:p-4 rounded-xl">
                        <p className="text-green-700 font-bold text-base md:text-lg">{t.transformation.dreams.multiple}</p>
                      </div>
                    )}
                    <div className="bg-white/80 p-3 md:p-4 rounded-xl">
                      <p className="text-green-700 font-bold text-base md:text-lg">{t.transformation.dreams.freedom}</p>
                    </div>
                    <div className="bg-white/80 p-3 md:p-4 rounded-xl">
                      <p className="text-green-700 font-bold text-base md:text-lg">{t.transformation.dreams.team}</p>
                    </div>
                    <div className="bg-white/80 p-3 md:p-4 rounded-xl">
                      <p className="text-green-700 font-bold text-base md:text-lg">{t.transformation.dreams.customers}</p>
                    </div>
                  </div>
                </div>
                
                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  {Object.entries(t.transformation.outcomes).map(([key, outcome], index) => (
                    <motion.div
                      key={key}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.15 }}
                      className="bg-white/80 p-6 rounded-xl shadow-lg border border-green-200"
                    >
                      <p className="text-green-700 font-bold text-lg">
                        {outcome && typeof outcome === 'string' ? outcome
                          .replace('{targetReviews}', results?.targetReviews || 0)
                          .replace('{currentReviews}', results?.currentReviews || 0)
                          .replace('{additionalRevenue}', formatCurrency(results?.additionalRevenue || 0).replace('$', ''))
                          : 'Loading...'
                        }
                      </p>
                    </motion.div>
                  ))}
                </div>

                <div className="bg-gradient-to-r from-[#9ACD32]/20 to-green-200/30 rounded-2xl p-6 mb-8">
                  <h4 className="text-2xl font-black text-gray-800 mb-4">🎯 {t.ui.transformedVision}</h4>
                  <p className="text-gray-700 text-lg leading-relaxed font-medium">
                    {t.transformation.vision}
                  </p>
                </div>

                <motion.button
                  onClick={nextStep}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-gradient-to-r from-[#9ACD32] to-green-600 text-white px-12 py-4 rounded-2xl font-black text-lg shadow-2xl hover:shadow-xl transition-all flex items-center mx-auto space-x-3"
                >
                  <span>{t.ui.seeRecommendedPlan}</span>
                  <TrendingUp className="w-5 h-5" />
                </motion.button>
              </div>
            </motion.div>
          )}

          {/* Step 3: Final Results with Plan Recommendation */}
          {currentStep === 3 && results && (
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="max-w-4xl mx-auto"
            >
              <h3 className="text-2xl md:text-3xl font-black text-center text-gray-800 mb-4">{t.results.title}</h3>
              
              {/* Plan Recommendation */}
              <div className="bg-gradient-to-r from-[#9ACD32] to-green-600 p-6 md:p-8 rounded-3xl text-black text-center mb-8 shadow-2xl">
                <h4 className="text-xl md:text-2xl font-black mb-4">{t.results.recommendedPlan}</h4>
                <div className="text-3xl md:text-4xl font-black mb-2">PLAN {results.recommendedPlan.toUpperCase()}</div>
                <p className="text-base md:text-lg font-semibold opacity-90 mb-4">
                  {t.ui.planExplanationTemplate} ({formData.numberOfLocations} {formData.numberOfLocations === 1 ? t.ui.location : t.ui.locations}, {formData.hasWaiters ? t.ui.with : t.ui.without} {t.ui.waitersLabel.toLowerCase()}, {formData.hasBar ? t.ui.with : t.ui.without} {t.ui.barLabel.toLowerCase()}):
                </p>
                <p className="text-sm md:text-base font-medium opacity-80">
                  {t.results.planQuestion && typeof t.results.planQuestion === 'string' ? t.results.planQuestion
                    .replace('{costPerOrder}', results?.costPerOrder?.toFixed(2) || '0.00')
                    .replace('{roi}', results?.roi?.toFixed(0) || '0')
                    : 'Loading...'
                  }
                </p>
              </div>
              
              {/* Key Metrics - Separated Fixed and Variable Costs */}
              <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                <div className="bg-blue-50 p-6 rounded-xl text-center border border-blue-200">
                  <div className="text-sm text-blue-600 font-semibold">{t.results.monthlyOrders}</div>
                  <div className="text-3xl font-black text-blue-800">
                    {results.monthlyOrders.toLocaleString()}
                  </div>
                </div>
                
                <div className="bg-orange-50 p-6 rounded-xl text-center border border-orange-200">
                  <div className="text-sm text-orange-600 font-semibold">{t.results.baseFee}</div>
                  <div className="text-3xl font-black text-orange-700">
                    {formatCurrency(results.baseFee)}
                  </div>
                  <div className="text-xs text-orange-500 font-medium">Fijo</div>
                </div>
                
                <div className="bg-red-50 p-6 rounded-xl text-center border border-red-200">
                  <div className="text-sm text-red-600 font-semibold">{t.results.variableFees}</div>
                  <div className="text-3xl font-black text-red-700">
                    {formatCurrency(results.variableFees)}
                  </div>
                  <div className="text-xs text-red-500 font-medium">Variables</div>
                </div>
                
                <div className="bg-green-50 p-6 rounded-xl text-center border border-green-200">
                  <div className="text-sm text-green-600 font-semibold">{t.results.additionalRevenue}</div>
                  <div className="text-3xl font-black text-green-700">
                    {formatCurrency(results.additionalRevenue)}
                  </div>
                </div>
                
                <div className="bg-[#9ACD32]/20 p-6 rounded-xl text-center border border-[#9ACD32]">
                  <div className="text-sm text-green-800 font-semibold">{t.results.netProfit}</div>
                  <div className="text-3xl font-black text-green-800">
                    {formatCurrency(results.netProfit)}
                  </div>
                </div>
              </div>

              {/* Total Investment Highlight */}
              <div className="bg-gradient-to-r from-gray-100 to-gray-200 p-6 rounded-2xl text-center mb-8 border border-gray-300">
                <div className="text-lg font-semibold text-gray-700">{t.results.totalInvestment}</div>
                <div className="text-4xl font-black text-gray-800">
                  {formatCurrency(results.kumiaFees)}
                </div>
                <div className="text-sm text-gray-600 font-medium">
                  ${results.costPerOrder.toFixed(2)} {t.ui.costPerOrder}
                </div>
              </div>

              {/* ROI Highlight with formatted percentage and explanation */}
              <div className="bg-gradient-to-r from-[#9ACD32] to-green-600 p-8 rounded-3xl text-black text-center mb-8 shadow-2xl">
                <div className="text-lg font-semibold opacity-90">{t.results.roi}</div>
                <div className="text-6xl font-black">
                  {results.roi.toLocaleString('es-ES', { maximumFractionDigits: 0 })}%
                </div>
                <div className="text-lg font-semibold opacity-90">
                  {t.results.paybackTime}: {results.paybackTime} días
                </div>
                <div className="text-sm font-medium opacity-75 mt-2 bg-black/10 rounded-lg p-3">
                  <strong>{t.ui.roiCalculation}:</strong> {t.ui.roiFormula}<br/>
                  {t.ui.roiDescription}
                </div>
              </div>

              {/* Benefits */}
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                {Object.entries(t.results.benefits).map(([key, benefit], index) => (
                  <motion.div
                    key={key}
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.1 }}
                    className="flex items-center space-x-3 bg-green-50 p-4 rounded-xl border border-green-200"
                  >
                    <CheckCircle className="w-6 h-6 text-[#9ACD32] flex-shrink-0" />
                    <span className="font-semibold text-gray-800">{benefit}</span>
                  </motion.div>
                ))}
              </div>

              {/* Final CTA */}
              <motion.button
                onClick={nextStep}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="w-full bg-gradient-to-r from-[#9ACD32] to-green-600 text-black py-6 px-8 rounded-3xl font-black text-2xl shadow-2xl hover:shadow-xl transition-all flex items-center justify-center space-x-4"
              >
                <Zap className="w-8 h-8" />
                <span>{t.results.getStarted}</span>
                <ArrowRight className="w-8 h-8" />
              </motion.button>
            </motion.div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default ROICalculator;